// Split caption text into plain-text and link segments so the composer
// previews can make URLs clickable (Scott, 2026-09-30: "click a URL in the
// preview to confirm it resolves where I expect").
//
// Pure, so every edge case is asserted without jsdom. The previews render the
// segments; they never build hrefs themselves.
//
// RULES (each has a test):
// - `http://` and `https://` URLs link as typed.
// - A bare `www.` host IS linked, deliberately, with `https://` prepended —
//   Facebook linkifies `www.example.com` in a published post, so leaving it
//   inert in our preview would misrepresent what publishes. It must look like
//   a real host (`www.<label>.<letter>…`); a lone `www.` stays text.
// - A `www.` that is part of something else (`awww.nice`, an email's domain,
//   a path segment already inside a URL) is left alone — the lookbehind stops
//   it matching mid-token, and the single left-to-right scan means a URL is
//   consumed whole, so `https://www.x.com` is ONE link, never two.
// - Trailing sentence punctuation (`.,;:!?'"`) is excluded from the href, and
//   so is a closing `)` / `]` that has no opener inside the URL — so
//   "(see https://x.com/a)" links `https://x.com/a`, while a Wikipedia-style
//   `https://x.com/Foo_(bar)` keeps its paren.
// - URL characters are printable ASCII only. An emoji straight after a URL
//   ("https://mazda.com.au🚗") ends it rather than being swallowed into it.

export type CaptionSegment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; text: string; href: string };

const URL_RE = /(https?:\/\/[!#-;=?-~]+|(?<![\w@./-])www\.[A-Za-z0-9-]+\.[A-Za-z][!#-;=?-~]*)/gi;

const TRAILING_PUNCT = new Set(['.', ',', ';', ':', '!', '?', "'", '"']);

function count(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n++;
  return n;
}

/** Strip trailing characters that belong to the sentence, not the URL. */
export function trimUrlTail(raw: string): string {
  let url = raw;
  for (;;) {
    const last = url.slice(-1);
    if (TRAILING_PUNCT.has(last)) {
      url = url.slice(0, -1);
      continue;
    }
    if (last === ')' && count(url, ')') > count(url, '(')) {
      url = url.slice(0, -1);
      continue;
    }
    if (last === ']' && count(url, ']') > count(url, '[')) {
      url = url.slice(0, -1);
      continue;
    }
    return url;
  }
}

export function linkifyCaption(text: string): CaptionSegment[] {
  const out: CaptionSegment[] = [];
  let cursor = 0;
  const pushText = (t: string) => {
    if (t === '') return;
    const prev = out[out.length - 1];
    if (prev && prev.kind === 'text') prev.text += t;
    else out.push({ kind: 'text', text: t });
  };
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const url = trimUrlTail(m[0]);
    // A match that trims down to a bare scheme ("https://." → "https://") is
    // not a link; leave it as text.
    if (/^https?:\/\/$/i.test(url)) continue;
    pushText(text.slice(cursor, start));
    const href = /^www\./i.test(url) ? `https://${url}` : url;
    out.push({ kind: 'link', text: url, href });
    cursor = start + url.length;
  }
  pushText(text.slice(cursor));
  return out;
}

/** The first link in a caption, or null — used by the Facebook link-card preview. */
export function firstCaptionLink(text: string): string | null {
  const seg = linkifyCaption(text).find((s) => s.kind === 'link');
  return seg && seg.kind === 'link' ? seg.href : null;
}
